import { useState } from 'react';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Material } from '@models/material.model';
import { graphQlClient } from '@config/graphqlClient';
import MaterialGraphQL from '@graphql/material.queries';

interface EditMaterialParams {
  materialId: string;
  name: string;
  supplier: string;
  time: string;
  amount: number;
  costPerUnit: number;
  pricePerUnit?: number | null;
  amountType: string;
  amountSystem: string;
  taskId: string;
  chargeToClient: boolean;
}

interface UpdateMaterialResponse {
  updateMaterial: Material;
}

interface UseEditMaterialReturn {
  editMaterial: (params: EditMaterialParams) => Promise<Material | null>;
  loading: boolean;
}

export const useEditMaterial = (): UseEditMaterialReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');

  const editMaterial = async (
    params: EditMaterialParams,
  ): Promise<Material | null> => {
    setLoading(true);

    try {
      const materialData = {
        materialId: params.materialId,
        name: params.name,
        supplier: params.supplier,
        time: params.time,
        amount: params.amount,
        costPerUnit: params.costPerUnit,
        pricePerUnit: params.pricePerUnit,
        amountType: params.amountType,
        amountSystem: params.amountSystem,
        taskId: params.taskId,
        chargeToClient: params.chargeToClient,
      };

      const data: UpdateMaterialResponse = await graphQlClient.request(
        MaterialGraphQL.mutations.updateMaterial,
        {
          material: materialData,
        },
      );

      if (data?.updateMaterial) {
        dispatch(
          appendActionMessage({
            message: localeCommon['updatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );

        return data.updateMaterial;
      }

      return null;
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message:
            error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { editMaterial, loading };
};
